import React, { useState, useEffect } from 'react';
import { useDataManagement } from '../ferramentas/RequisitarAPI.ts';
import useDebounce from '../ferramentas/useDebounce.js';

// Importando os componentes modularizados
import { ErrorMessage, NoDataMessage } from './components/TableFeedback';
import UserSelectionModal from './components/UserSelectionModal';

const formatSafeDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('-').map(p => p.padStart(2, '0')).join('-');
};

const CompromissosSection = ({ styles, formatCurrency, initialDateRange }) => {
    // Hooks para interagir com a API
    const { reports, users, load, error } = useDataManagement();
    
    // Estado principal de filtros
    const [filters, setFilters] = useState({
        start_date: initialDateRange.inicio,
        final_date: initialDateRange.fim,
        customers: '', // Para o debounce
        users: [], // Array de IDs de funcionários
        page: 1,
        limit: 999999999,
    });

    // --- ESTADOS DE CONTROLE DE UI E DADOS ---
    const [customerInput, setCustomerInput] = useState('');
    const debouncedCustomerInput = useDebounce(customerInput, 500);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [funcionarios, setFuncionarios] = useState([]);

    // --- EFEITOS (LÓGICA) ---

    // 1. Busca a lista de funcionários para o modal (apenas uma vez)
    useEffect(() => {
        const fetchFuncionarios = async () => {
            const response = await users.list();
            if (response.ok && response.data) {
                const userList = Object.values(response.data).map(user => ({ id: user.id, nome: user.fullname }));
                setFuncionarios(userList);
            }
        };
        fetchFuncionarios();
    }, [users]);

    // 2. Efeito para atualizar os filtros quando o input de cliente "atrasado" muda
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            customers: debouncedCustomerInput,
            page: 1,
        }));
    }, [debouncedCustomerInput]);

    // 3. Busca os dados do relatório sempre que os filtros mudam
    useEffect(() => {
        const fetchReport = async () => {
            const customersArray = filters.customers
                ? filters.customers.split(',').map(c => c.trim()).filter(Boolean)
                : [];

            const apiFilters = {
                start_date: filters.start_date || undefined,
                final_date: filters.final_date || undefined,
                customers: customersArray.length > 0 ? customersArray : undefined,
                users: filters.users.length > 0 ? filters.users : undefined,
                page: filters.page,
                limit: parseInt(filters.limit, 10),
            };
            
            const response = await reports.appointments(apiFilters);
            
            if (!response.ok) {
                console.error('Erro ao buscar relatório de compromissos:', response.message);
            }
            setReportData(response.ok ? response.data : null);
        };
        fetchReport();
    }, [filters, reports]);


    // --- MANIPULADORES DE EVENTOS ---
    const handleNonDebouncedFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1
        }));
    };
    const handleUserSelectionConfirm = (newUsers) => setFilters(prev => ({ ...prev, users: newUsers, page: 1 }));
    const handleNextPage = () => setFilters(prev => ({ ...prev, page: prev.page + 1 }));
    const handlePreviousPage = () => setFilters(prev => ({ ...prev, page: prev.page - 1 }));

    // --- FUNÇÕES AUXILIARES DE RENDERIZAÇÃO ---
    // A API parece paginar por cliente, então usamos 'total' se disponível, ou o tamanho do array como fallback.
    const totalPages = reportData ? Math.ceil(reportData.total / filters.limit) : 1; 

    const getUserButtonText = () => {
        if (filters.users.length === 0) return 'Todos os Funcionários';
        if (filters.users.length === 1) return '1 funcionário selecionado';
        return `${filters.users.length} funcionários selecionados`;
    };

    // --- RENDERIZAÇÃO DO COMPONENTE (JSX) ---

    return (
        <>
            <UserSelectionModal
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                allUsers={funcionarios}
                selectedUsers={filters.users}
                onConfirm={handleUserSelectionConfirm}
                styles={styles}
            />

            <div id="compromissos" className={`${styles.tabContent} ${styles.activeContent} ${styles.card}`}>
                <h2 className={styles.cardTitle}>Relatório Detalhado de Compromissos</h2>
                
                <div className={`${styles.filterSection} ${styles.colsMd2} ${styles.colsLg4}`}>
                    <div>
                        <label>Data Início</label>
                        <input type="date" name="start_date" value={filters.start_date} onChange={handleNonDebouncedFilterChange} />
                    </div>
                    <div>
                        <label>Data Fim</label>
                        <input type="date" name="final_date" value={filters.final_date} onChange={handleNonDebouncedFilterChange} />
                    </div>
                    <div>
                        <label>Cliente(s)</label>
                        <input
                            type="text"
                            name="customers"
                            value={customerInput}
                            onChange={(e) => setCustomerInput(e.target.value)}
                            placeholder="Buscar por nome de cliente..."
                        />
                    </div>
                    <div>
                        <label>Funcionário(s)</label>
                        <button type="button" className={styles.modalTriggerButton} onClick={() => setIsUserModalOpen(true)}>
                            {getUserButtonText()}
                        </button>
                    </div>
                    {/* <div>
                        <label>Itens por Página</label>
                        <select name="limit" value={filters.limit} onChange={handleNonDebouncedFilterChange}>
                            <option value="9999999">todos</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                        </select>
                    </div> desativado por falta de necessidade*/}
                </div>

                <div className={styles.spaceY6}>
                    {load.isLoading ? 
                        <div id="loading-overlay">
                            <div>
                            <div id="loading-spinner"></div>
                            <p>{load.reason}</p>
                            </div>
                        </div>
                     : error ? <ErrorMessage error={error} styles={styles} />
                     : reportData?.data?.length > 0 ? (
                        reportData.data.map(customerData => (
                            <details key={customerData.customer} className={styles.appointmentGroup} open>
                                <summary className={styles.appointmentSummary}>
                                    <span className={styles.customerName}>{customerData.customer}</span>
                                    <span className={styles.customerSubtotal}>
                                        {customerData.cards.length} compromisso(s) - Total: {formatCurrency(customerData.subtotal)}
                                    </span>
                                </summary>
                                <div className={styles.tableResponsiveWrapper}>
                                    <table className={`${styles.table} ${styles.nestedTable}`}>
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Hora</th>
                                                <th>Funcionário</th>
                                                <th>Serviços</th>
                                                <th className={styles.textRight}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {customerData.cards
                                                .sort((a, b) => new Date(formatSafeDate(b.date) + 'T00:00:00Z') - new Date(formatSafeDate(a.date) + 'T00:00:00Z')) // Ordena compromissos por data (mais recente primeiro)
                                                .map(card => (
                                                    <tr key={card.card_id}>
                                                        <td>{new Date(formatSafeDate(card.date) + 'T00:00:00Z').toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</td>
                                                        <td>{card.time}</td>
                                                        <td>{card.user}</td>
                                                        <td>{card.services.join(', ')}</td>
                                                        <td className={styles.textRight}>{formatCurrency(card.total)}</td>
                                                    </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </details>
                        ))
                    ) : <NoDataMessage styles={styles} />}
                </div>

                {!load.isLoading && !error && reportData && reportData.total > filters.limit && (
                    <div className={styles.paginationControls}>
                        <button onClick={handlePreviousPage} disabled={filters.page <= 1}>Anterior</button>
                        <span>Página {filters.page} de {totalPages}</span>
                        <button onClick={handleNextPage} disabled={filters.page >= totalPages}>Próxima</button>
                    </div>
                )}
            </div>
        </>
    );
};

export default CompromissosSection;